<template>
  <div>
    <transition name="fade">
      <div class="modal" v-show="isOpen">
        <transition name="drop-in">
          <div class="modal-inner" v-show="isOpen" ref="target">
            <div class="modal-content">
              <!-- Header with PDF Export Button -->
              <div class="header">
                <div class="header-content">
                  <h1 class="week-title">{{ title }}</h1>
                  <button
                    @click="generatePDF"
                    class="pdf-button"
                    :disabled="
                      !event?.existProgram || planningStore.getPlanningLoading
                    "
                    v-if="event?.existProgram"
                  >
                    <svg
                      class="pdf-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path
                        d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                      />
                    </svg>
                    {{ t("exportPDF") || "Export PDF" }}
                  </button>
                </div>
              </div>

              <div v-if="!event?.existProgram" class="no-program-container">
                <div class="no-program-message">
                  <p class="text-gray-500 text-center py-8">
                    {{ t("noProgramAvailable") || "No Program Available" }}
                  </p>
                </div>
              </div>

              <div v-if="event?.existProgram" class="program-container">
                <!-- Tabs Section -->
                <div class="tabs-section">
                  <div class="tabs-container">
                    <button
                      v-for="(program, index) in mergedPrograms"
                      :key="index"
                      :class="{
                        active: selectedProgramName === program.programName,
                      }"
                      @click="onTabClick(program.programName, program.ids)"
                      class="tab-button"
                    >
                      {{ program.programName }}
                    </button>
                  </div>
                </div>

                <div class="content-section">
                  <!-- Date Information - Show dates when selected program exists -->
                  <div class="date-info-section">
                    <div class="date-grid">
                      <div class="date-column">
                        <h3 class="date-header">
                          {{ t("dateStart") || "Date Start" }}
                        </h3>
                        <div class="date-list">
                          <div
                            v-for="(
                              date, index
                            ) in selectedProgramDates.startDates"
                            :key="`start-${index}`"
                            class="date-item"
                            :class="{ 'highlight-today': isToday(date) }"
                          >
                            <span class="date-bullet">•</span>
                            <span class="date-text">{{
                              formatDate(date)
                            }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="date-column">
                        <h3 class="date-header">
                          {{ t("dateEnd") || "Date End" }}
                        </h3>
                        <div class="date-list">
                          <div
                            v-for="(
                              date, index
                            ) in selectedProgramDates.endDates"
                            :key="`end-${index}`"
                            class="date-item"
                            :class="{ 'highlight-today': isToday(date) }"
                          >
                            <span class="date-bullet">•</span>
                            <span class="date-text">{{
                              formatDate(date)
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Start and Stop Time Information for each cycle group -->
                  <div
                    v-if="cyclesStation.length > 0"
                    class="time-info-section"
                  >
                    <div class="time-grid">
                      <!-- Start Time Column -->
                      <div class="time-column">
                        <h3
                          class="time-header text-lg font-semibold text-gray-800 mb-2"
                        >
                          {{ t("startTime") || "Start Time" }}
                        </h3>
                        <div class="time-list space-y-1">
                          <div
                            v-for="(cycleStations, idx) in cyclesStation"
                            :key="`start-${idx}`"
                            class="time-item text-sm text-blue-600 font-medium"
                          >
                            <span
                              v-if="
                                planning.startTime && planning.startTime[idx]
                              "
                              class="inline-block bg-blue-50 rounded px-2 py-1"
                            >
                              • {{ planning.startTime[idx].hour }}:{{
                                planning.startTime[idx].minute
                                  .toString()
                                  .padStart(2, "0")
                              }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Stop Time Column -->
                      <div class="time-column">
                        <h3
                          class="time-header text-lg font-semibold text-gray-800 mb-2"
                        >
                          {{ t("stopTime") || "Stop Time" }}
                        </h3>
                        <div class="time-list space-y-1">
                          <div
                            v-for="(cycleStations, idx) in cyclesStation"
                            :key="`stop-${idx}`"
                            class="time-item text-sm text-red-600 font-medium"
                          >
                            <span
                              v-if="cycleStations.length > 0"
                              class="inline-block bg-red-50 rounded px-2 py-1"
                            >
                              •
                              {{
                                formatTime(
                                  cycleStations[cycleStations.length - 1]
                                    .stopTime
                                )
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Station Schedule Section: Render all cycles as separate tables with filtered content -->
                  <div v-if="cyclesStation.length > 0">
                    <div
                      v-for="(cycleStations, idx) in cyclesStation"
                      :key="`cycle-group-${idx}`"
                      class="schedule-section"
                    >
                      <h3 class="schedule-title">
                        {{
                          t("schedule.startTitle", {
                            cycleNumber: idx + 1,
                            ordinal: getOrdinalSuffix(idx + 1),
                          })
                        }}
                      </h3>
                      <p class="schedule-subtitle">
                        {{ t("stationActive") || "Station Active -" }}
                        {{ idx + 1 }} {{ t("cycles") || "cycles" }}
                      </p>

                      <!-- Cycle Navigation Controls -->
                      <div class="cycle-navigation-controls">
                        <div class="cycle-info">
                          <span class="cycle-display">
                            {{ t("showingCycle") || "Showing Cycle:" }}
                            {{ getCurrentCycleValue(cycleStations, idx) }} ({{
                              getCurrentCycleIndex(idx) + 1
                            }}
                            {{ t("of") || "of" }}
                            {{ getUniqueCycles(cycleStations).length }})
                          </span>
                        </div>
                        <div class="navigation-buttons">
                          <button
                            @click="previousCycle(idx)"
                            :disabled="getCurrentCycleIndex(idx) <= 0"
                            class="cycle-nav-btn prev-btn"
                            :class="{
                              disabled: getCurrentCycleIndex(idx) <= 0,
                            }"
                          >
                            ← {{ t("previousCycle") || "Previous Cycle" }}
                          </button>
                          <button
                            @click="nextCycle(idx)"
                            :disabled="
                              getCurrentCycleIndex(idx) >=
                              getUniqueCycles(cycleStations).length - 1
                            "
                            class="cycle-nav-btn next-btn"
                            :class="{
                              disabled:
                                getCurrentCycleIndex(idx) >=
                                getUniqueCycles(cycleStations).length - 1,
                            }"
                          >
                            {{ t("nextCycle") || "Next Cycle" }} →
                          </button>
                        </div>
                      </div>

                      <div
                        v-if="planningStore.getPlanningLoading"
                        class="loading-container"
                      >
                        <div class="loading-skeleton"></div>
                      </div>

                      <table class="station-table">
                        <thead>
                          <tr>
                            <th class="station-header">
                              {{ t("stations") || "Stations" }}
                            </th>
                            <th class="station-header">
                              {{ modeLabel }}
                            </th>
                            <th class="station-header">
                              {{ t("startStopTime") }}
                            </th>

                            <th class="station-header">
                              {{ t("cycles") || "Cycles" }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Filter stations to show only current cycle -->
                          <tr
                            v-for="station in getFilteredStations(
                              cycleStations,
                              idx
                            )"
                            :key="`${station.field}-${station.cycle}`"
                          >
                            <td class="station-cell">
                              {{ station.groupName || station.stationName }}
                            </td>
                            <td class="station-cell">
                              {{ formatStationTime(station) }}
                            </td>
                            <td class="station-cell">
                              {{ formatOnlyTime(station.startTime) }} -
                              {{ formatOnlyTime(station.stopTime) }}
                            </td>
                            <td class="station-cell">
                              {{ station.cycle }}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Show empty state if no stations for current cycle -->
                      <div
                        v-if="
                          getFilteredStations(cycleStations, idx).length === 0
                        "
                        class="empty-cycle-state"
                      >
                        <p class="empty-cycle-message">
                          {{
                            t("noStationsAvailableForCycle") ||
                            "No stations available for Cycle"
                          }}
                          {{ getCurrentCycleValue(cycleStations, idx) }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Empty state for stations -->
                  <div
                    v-else-if="!planningStore.getPlanningLoading"
                    class="no-data"
                  >
                    <p class="no-data-text">
                      {{ t("noStationAvailable") || "No Station Available" }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { ref, watch, computed } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { usePlanningStore } from "@/stores/planning/PlanningStore";
import jsPDF from "jspdf";

//stores
const planningStore = usePlanningStore();
const { planning } = storeToRefs(usePlanningStore());

const { t } = useI18n();

const props = defineProps({
  isOpen: Boolean,
  title: String,
  id: String,
  deviceCode: String,
  event: Object,
});

const selectedProgramName = ref("");
const selectedProgramIds = ref([]);
const currentCycle = ref(0);
const currentCycleIndex = ref({});

// Computed property to merge programs with same name
const mergedPrograms = computed(() => {
  if (!props.event?.allProgram) return [];

  const programMap = new Map();

  props.event.allProgram.forEach((program) => {
    const name = program.programName;
    if (programMap.has(name)) {
      // Merge with existing program
      const existing = programMap.get(name);
      existing.ids.push(program.id);
      existing.startDates.push(program.start);
      existing.endDates.push(program.end);
      existing.programs.push(program);
    } else {
      // Create new merged program entry
      programMap.set(name, {
        programName: name,
        ids: [program.id],
        startDates: [program.start],
        endDates: [program.end],
        programs: [program],
      });
    }
  });

  return Array.from(programMap.values());
});

// Computed property to get dates for selected program
const selectedProgramDates = computed(() => {
  const selectedProgram = mergedPrograms.value.find(
    (program) => program.programName === selectedProgramName.value
  );
  const selected = mergedPrograms.value.find(
    (program) => program.programName === selectedProgramName.value
  );

  if (!selectedProgram) {
    return { startDates: [], endDates: [] };
  }

  return {
    startDates: selectedProgram.startDates || [],
    endDates: selectedProgram.endDates || [],
  };
});

// Computed property for total cycles
const totalCycles = computed(() => {
  if (!planning.value.stations) return 0;
  return planning.value.stations.length;
});

// Computed property for current cycle stations
const currentCycleStations = computed(() => {
  if (
    !planning.value.stations ||
    !planning.value.stations[currentCycle.value]
  ) {
    return [];
  }
  return planning.value.stations[currentCycle.value];
});

// Computed property for current cycle title
const currentCycleTitle = computed(() => {
  const cycleNumber = currentCycle.value + 1;
  return `${cycleNumber}${getOrdinalSuffix(cycleNumber)} Schedule Start`;
});

// Computed property for current cycle number display
const currentCycleNumber = computed(() => {
  return currentCycle.value + 1;
});

// Group stations by their cycles value for table rendering (2D array)
const cyclesStation = computed(() => planning.value.stations || []);
const modeMap = {
  "hour/minute": t("mode_hour_minute"),
  "minute/second": t("mode_minute_second"),
  // Add more as needed
};

const modeLabel = computed(() => {
  const mode = cyclesStation[0]?.mode || planning.mode || getTimeHeader();
  return modeMap[mode] || mode; // fallback to raw string if no match
});
// Log cyclesStation value on change
watch(cyclesStation, (newVal) => {}, { immediate: true, deep: true });

// Helper to get day name from date string
function getDayName(dateString) {
  if (!dateString) return "";
  const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const dateObj = new Date(year, month, day);
  // Use i18n for day name if available
  const dayNames = [
    t("day_sun") || "Sunday",
    t("day_mon") || "Monday",
    t("day_tue") || "Tuesday",
    t("day_wed") || "Wednesday",
    t("day_thu") || "Thursday",
    t("day_fri") || "Friday",
    t("day_sat") || "Saturday",
  ];
  return dayNames[dateObj.getDay()];
}

// Helper to check if date is today
function isToday(dateString) {
  if (!dateString) return false;
  const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return false;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const dateObj = new Date(year, month, day);
  const today = new Date();
  return (
    dateObj.getFullYear() === today.getFullYear() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getDate() === today.getDate()
  );
}

// Helper function to get ordinal suffix
function getOrdinalSuffix(num) {
  const j = num % 10;
  const k = num % 100;
  if (j == 1 && k != 11) return "st";
  if (j == 2 && k != 12) return "nd";
  if (j == 3 && k != 13) return "rd";
  return "th";
}

// Helper function to format date
function formatDate(dateString) {
  if (!dateString) return "";
  const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateString;
  const year = parseInt(match[1], 10);
  const monthNum = parseInt(match[2], 10);
  const dayNum = parseInt(match[3], 10);
  const dateObj = new Date(year, monthNum - 1, dayNum);
  // i18n day and month names
  const dayNames = [
    t("day_sun") || "Sunday",
    t("day_mon") || "Monday",
    t("day_tue") || "Tuesday",
    t("day_wed") || "Wednesday",
    t("day_thu") || "Thursday",
    t("day_fri") || "Friday",
    t("day_sat") || "Saturday",
  ];
  const monthNames = [
    t("month_jan") || "January",
    t("month_feb") || "February",
    t("month_mar") || "March",
    t("month_apr") || "April",
    t("month_may") || "May",
    t("month_jun") || "June",
    t("month_jul") || "July",
    t("month_aug") || "August",
    t("month_sep") || "September",
    t("month_oct") || "October",
    t("month_nov") || "November",
    t("month_dec") || "December",
  ];
  const dayName = dayNames[dateObj.getDay()];
  const monthName = monthNames[monthNum - 1];
  return `${dayName} ${dayNum} ${monthName} ${year}`;
}

function formatOnlyTime(value) {
  if (!value) return "-";
  const match = value.match(/(\d{2}:\d{2})/);
  return match ? match[0] : value;
}
// Helper function to format time
function formatTime(timeString) {
  if (!timeString) return "";
  const match = timeString.match(/(\d{2}:\d{2})/);
  return match ? match[0] : timeString;
}

// Helper function to get time header based on mode
function getTimeHeader() {
  if (planning.value.mode === 1) {
    return `${t("minute") || "Minute"}/${t("second") || "Second"}`;
  } else if (planning.value.mode === 2) {
    return `${t("hour") || "Hour"}/${t("minute") || "Minute"}`;
  }
  return `${t("hour") || "Hour"}/${t("minute") || "Minute"}`;
}

// Helper function to format station time
function formatStationTime(station) {
  if (!station.value) return "";

  const timeParts = station.value.split(".");
  const firstPart = timeParts[0] || "0";
  const secondPart = timeParts[1] || "0";

  return `${firstPart}.${secondPart}`;
}

// Helper function to get total cycles for a station
function getTotalCycles(station) {
  // This should be adjusted based on your actual data structure
  return station.totalCycles || totalCycles.value || 1;
}

// Tab click function - now handles merged programs
function onTabClick(programName, programIds) {
  // console.log(`[WeeklySchedule] Tab clicked:`, {
  //   programName: programName,
  //   programIds: programIds,
  //   deviceCode: props.deviceCode,
  // });

  selectedProgramName.value = programName;
  selectedProgramIds.value = programIds;
  currentCycle.value = 0;
  // Reset all cycle indices to 0 when changing program
  Object.keys(currentCycleIndex.value).forEach((key) => {
    currentCycleIndex.value[key] = 0;
  });
  planningStore.planning = [];
  const firstProgramId = programIds[0];
  planningStore.getPlanning(firstProgramId, { deviceCode: props.deviceCode });

  console.log(
    `[WeeklySchedule] Updated selectedProgramName to: ${programName}, using ID: ${firstProgramId}`
  );
}

// Watch for modal opening
watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      // console.log("[WeeklySchedule] Modal opened with props:", {
      //   isOpen: props.isOpen,
      //   title: props.title,
      //   id: props.id,
      //   deviceCode: props.deviceCode,
      //   event: props.event,
      // });

      // Reset state when opening
      currentCycle.value = 0;

      // The first program will be set active by the mergedPrograms watcher below
    } else {
      // Reset when modal closes
      selectedProgramName.value = "";
      selectedProgramIds.value = [];
      currentCycle.value = 0;
    }
  }
);

// Helper Index Navigation
function getUniqueCycles(cycleStations) {
  if (!cycleStations || cycleStations.length === 0) return [];
  const cycles = [...new Set(cycleStations.map((station) => station.cycle))];
  return cycles.sort((a, b) => a - b);
}

// Get current cycle index for a specific table
function getCurrentCycleIndex(tableIdx) {
  return currentCycleIndex.value[tableIdx] || 0;
}

// Get current cycle value for a specific table
function getCurrentCycleValue(cycleStations, tableIdx) {
  const uniqueCycles = getUniqueCycles(cycleStations);
  const index = getCurrentCycleIndex(tableIdx);
  return uniqueCycles[index] || 1;
}
// Get filtered stations for current cycle of a specific table
function getFilteredStations(cycleStations, tableIdx) {
  if (!cycleStations || cycleStations.length === 0) return [];

  const currentCycleValue = getCurrentCycleValue(cycleStations, tableIdx);
  return cycleStations.filter((station) => station.cycle === currentCycleValue);
}

// Navigation functions
function previousCycle(tableIdx) {
  const currentIndex = getCurrentCycleIndex(tableIdx);
  if (currentIndex > 0) {
    currentCycleIndex.value[tableIdx] = currentIndex - 1;
    const cycleStations = cyclesStation.value[tableIdx];
    const newCycleValue = getCurrentCycleValue(cycleStations, tableIdx);
    console.log(
      `[Table ${tableIdx + 1}] Navigate to previous cycle: ${newCycleValue}`
    );
  }
}

function nextCycle(tableIdx) {
  const cycleStations = cyclesStation.value[tableIdx];
  const uniqueCycles = getUniqueCycles(cycleStations);
  const currentIndex = getCurrentCycleIndex(tableIdx);

  if (currentIndex < uniqueCycles.length - 1) {
    currentCycleIndex.value[tableIdx] = currentIndex + 1;
    const newCycleValue = getCurrentCycleValue(cycleStations, tableIdx);
    console.log(
      `[Table ${tableIdx + 1}] Navigate to next cycle: ${newCycleValue}`
    );
  }
}

// Initialize cycle indices for all tables
function initializeCycleIndices() {
  cyclesStation.value.forEach((cycleStations, idx) => {
    if (!currentCycleIndex.value.hasOwnProperty(idx)) {
      currentCycleIndex.value[idx] = 0;
    }
  });
}

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      // console.log("[WeeklySchedule] Modal opened with props:", {
      //   isOpen: props.isOpen,
      //   title: props.title,
      //   id: props.id,
      //   deviceCode: props.deviceCode,
      //   event: props.event,
      // });
      currentCycle.value = 0;
      currentCycleIndex.value = {};
    } else {
      selectedProgramName.value = "";
      selectedProgramIds.value = [];
      currentCycle.value = 0;
      currentCycleIndex.value = {};
    }
  }
);
watch(
  cyclesStation,
  (newVal) => {
    if (newVal && newVal.length > 0) {
      initializeCycleIndices();
    }
  },
  { immediate: true, deep: true }
);

watch(
  mergedPrograms,
  (newMergedPrograms) => {
    if (
      props.isOpen &&
      newMergedPrograms.length > 0 &&
      !selectedProgramName.value
    ) {
      const firstMergedProgram = newMergedPrograms[0];

      selectedProgramName.value = firstMergedProgram.programName;
      selectedProgramIds.value = firstMergedProgram.ids;

      planningStore.planning = [];
      planningStore.getPlanning(firstMergedProgram.ids[0], {
        deviceCode: props.deviceCode,
      });
    }
  },
  { immediate: true }
);

watch(
  () => planning.value.startTime,
  (newStartTime) => {},
  { immediate: true, deep: true }
);

const emits = defineEmits(["close"]);

const target = ref(null);

onClickOutside(target, () => {
  if (props.isOpen) {
    emits("close");
  }
});

const startTimes = computed(() => {
  return planning.value.startTime || [];
});
const generatePDF = async () => {
  try {
    console.log("Starting PDF generation for all programs...");

    const doc = new jsPDF();
    let yPosition = 30;
    const pageHeight = doc.internal.pageSize.height;
    const leftMargin = 25;
    const rightMargin = 25;
    const pageWidth = doc.internal.pageSize.width - leftMargin - rightMargin;

    // Color palette
    const colors = {
      primary: [41, 128, 185],
      secondary: [52, 73, 94],
      accent: [231, 76, 60],
      lightGray: [236, 240, 241],
      mediumGray: [149, 165, 166],
      darkGray: [44, 62, 80],
      white: [255, 255, 255],
    };

    const checkNewPage = (requiredSpace = 25) => {
      if (yPosition + requiredSpace > pageHeight - 40) {
        doc.addPage();
        addPageHeader();
        yPosition = 50;
        return true;
      }
      return false;
    };

    const safeText = (text, fallback = "N/A") => {
      try {
        return text && typeof text === "string"
          ? text
          : String(text || fallback);
      } catch (e) {
        return fallback;
      }
    };

    const addPageHeader = () => {
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, doc.internal.pageSize.width, 8, "F");

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.white);
      doc.text(safeText(props.title, "Weekly Schedule"), leftMargin, 6);

      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      doc.text(
        `Page ${pageNum}`,
        doc.internal.pageSize.width - rightMargin - 15,
        6
      );
    };

    const drawTable = (headers, data, startY, options = {}) => {
      const {
        headerBgColor = colors.primary,
        headerTextColor = colors.white,
        alternateRowColor = colors.lightGray,
        borderColor = colors.mediumGray,
        fontSize = 9,
      } = options;

      const colWidth = pageWidth / headers.length;
      const rowHeight = 12;
      let currentY = startY;

      doc.setFontSize(fontSize + 1);
      doc.setFont("helvetica", "bold");

      doc.setFillColor(...headerBgColor);
      doc.rect(leftMargin, currentY, pageWidth, rowHeight, "F");

      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.5);
      doc.rect(leftMargin, currentY, pageWidth, rowHeight);

      doc.setTextColor(...headerTextColor);
      headers.forEach((header, index) => {
        const x = leftMargin + index * colWidth + 4;
        doc.text(safeText(header), x, currentY + 8);
      });

      currentY += rowHeight;
      doc.setTextColor(...colors.darkGray);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fontSize);

      data.forEach((row, rowIndex) => {
        if (currentY + rowHeight > pageHeight - 40) {
          doc.addPage();
          addPageHeader();
          currentY = 50;
          yPosition = 50;
        }

        if (rowIndex % 2 === 1) {
          doc.setFillColor(...alternateRowColor);
          doc.rect(leftMargin, currentY, pageWidth, rowHeight, "F");
        }

        doc.setDrawColor(...borderColor);
        doc.rect(leftMargin, currentY, pageWidth, rowHeight);

        row.forEach((cell, cellIndex) => {
          const x = leftMargin + cellIndex * colWidth + 4;
          let cellText = safeText(cell);

          if (cellText.includes("--- Cycle")) {
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...colors.secondary);
          } else {
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...colors.darkGray);
          }
          const maxLength = Math.floor(colWidth / 2.5);
          if (cellText.length > maxLength) {
            cellText = cellText.substring(0, maxLength - 3) + "...";
          }

          doc.text(cellText, x, currentY + 8);
        });

        currentY += rowHeight;
      });

      yPosition = currentY + 15;
    };

    const addHeader = () => {
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, doc.internal.pageSize.width, 20, "F");

      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...colors.white);
      const title = safeText(props.title, "Weekly Schedule - All Programs");
      doc.text(title, leftMargin, 14);

      doc.setFillColor(...colors.accent);
      doc.rect(leftMargin, 22, 60, 2, "F");

      yPosition = 35;
    };

    const addDocumentInfo = () => {
      checkNewPage(40);

      const now = new Date();
      const totalPrograms = mergedPrograms.value?.length || 0;
      const info = [
        { label: "Generated:", value: now.toLocaleDateString() },
        { label: "Time:", value: now.toLocaleTimeString() },
        {
          label: "Total Programs:",
          value: `${totalPrograms} program${totalPrograms !== 1 ? "s" : ""}`,
        },
      ];

      doc.setFillColor(...colors.lightGray);
      doc.roundedRect(leftMargin, yPosition, pageWidth, 25, 3, 3, "F");

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.secondary);

      info.forEach((item, index) => {
        const x = leftMargin + 8 + index * (pageWidth / 3);
        doc.setFont("helvetica", "bold");
        doc.text(item.label, x, yPosition + 10);
        doc.setFont("helvetica", "normal");
        doc.text(item.value, x, yPosition + 18);
      });

      yPosition += 35;
    };

    // Function to get planning data for a program
    const getProgramPlanningData = async (programId) => {
      try {
        const currentPlanning = { ...planning.value };
        planningStore.planning = [];
        await planningStore.getPlanning(programId, {
          deviceCode: props.deviceCode,
        });
        await new Promise((resolve) => setTimeout(resolve, 500));
        const programPlanning = { ...planning.value };
        planningStore.planning = currentPlanning;

        return programPlanning;
      } catch (error) {
        console.warn(
          `Error fetching planning data for program ${programId}:`,
          error
        );
        return null;
      }
    };

    console.log("Adding header...");
    addHeader();
    addDocumentInfo();

    if (!props.event?.existProgram || !mergedPrograms.value?.length) {
      checkNewPage(30);

      doc.setFillColor(254, 249, 195);
      doc.roundedRect(leftMargin, yPosition, pageWidth, 30, 3, 3, "F");

      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.secondary);
      doc.text("⚠ No Program Data Available", leftMargin + 10, yPosition + 20);

      const fileName = `${safeText(props.title).replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}_all_programs.pdf`;
      doc.save(fileName);
      console.log("PDF saved - No program data");
      return;
    }

    // Process all merged programs
    for (
      let programIndex = 0;
      programIndex < mergedPrograms.value.length;
      programIndex++
    ) {
      const program = mergedPrograms.value[programIndex];

      try {
        console.log(
          `Processing program ${programIndex + 1}/${
            mergedPrograms.value.length
          }: ${program.programName}`
        );

        // Add program section header
        checkNewPage(50);

        doc.setFillColor(...colors.accent);
        doc.rect(leftMargin, yPosition, pageWidth, 12, "F");

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...colors.white);
        doc.text(
          `Program ${programIndex + 1}: ${safeText(program.programName)}`,
          leftMargin + 8,
          yPosition + 9
        );
        yPosition += 22;

        // Add program dates information
        if (program.startDates?.length > 0 || program.endDates?.length > 0) {
          checkNewPage(60);

          // Section header
          doc.setFillColor(...colors.secondary);
          doc.rect(leftMargin, yPosition, pageWidth, 8, "F");

          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...colors.white);
          doc.text("Schedule Information", leftMargin + 5, yPosition + 6);
          yPosition += 18;

          // Two-column layout for dates
          const columnWidth = pageWidth / 2;
          const leftColumnX = leftMargin;
          const rightColumnX = leftMargin + columnWidth + 10;
          const headerY = yPosition;

          doc.setFontSize(10);
          doc.setTextColor(...colors.darkGray);

          // Column headers
          doc.setFont("helvetica", "bold");
          doc.text("Start Dates", leftColumnX, headerY);
          doc.text("End Dates", rightColumnX, headerY);
          yPosition += 12;

          // Calculate max rows needed
          const maxStartDates = program.startDates?.length || 0;
          const maxEndDates = program.endDates?.length || 0;
          const maxRows = Math.max(maxStartDates, maxEndDates);

          doc.setFont("helvetica", "normal");

          // Display dates in parallel columns
          for (let i = 0; i < maxRows; i++) {
            checkNewPage();

            // Start date column
            if (i < maxStartDates && program.startDates[i]) {
              try {
                const formattedDate = formatDate(program.startDates[i]);
                doc.text(`• ${formattedDate}`, leftColumnX + 8, yPosition);
              } catch (err) {
                doc.text(
                  `• ${program.startDates[i]}`,
                  leftColumnX + 8,
                  yPosition
                );
              }
            }

            // End date column
            if (i < maxEndDates && program.endDates[i]) {
              try {
                const formattedDate = formatDate(program.endDates[i]);
                doc.text(`• ${formattedDate}`, rightColumnX + 8, yPosition);
              } catch (err) {
                doc.text(
                  `• ${program.endDates[i]}`,
                  rightColumnX + 8,
                  yPosition
                );
              }
            }

            yPosition += 6;
          }

          yPosition += 15;
        }

        // Get planning data for this program
        const programPlanningData = await getProgramPlanningData(
          program.ids[0]
        );

        if (
          programPlanningData?.stations &&
          Array.isArray(programPlanningData.stations) &&
          programPlanningData.stations.length > 0
        ) {
          console.log(
            `Adding station data for program: ${program.programName}`
          );

          programPlanningData.stations.forEach((cycleStations, idx) => {
            try {
              if (!Array.isArray(cycleStations) || cycleStations.length === 0)
                return;

              checkNewPage(50);

              // Schedule group header
              doc.setFillColor(...colors.secondary);
              doc.rect(leftMargin, yPosition, pageWidth, 10, "F");

              doc.setFontSize(13);
              doc.setFont("helvetica", "bold");
              doc.setTextColor(...colors.white);
              doc.text(
                `Schedule Group ${idx + 1}`,
                leftMargin + 5,
                yPosition + 7
              );
              yPosition += 20;

              // Time information
              try {
                let timeInfo = "";
                if (programPlanningData?.startTime?.[idx]) {
                  const startHour =
                    programPlanningData.startTime[idx].hour || 0;
                  const startMinute =
                    programPlanningData.startTime[idx].minute || 0;
                  const startTime = `${startHour}:${String(
                    startMinute
                  ).padStart(2, "0")}`;
                  timeInfo += `Start: ${startTime}`;
                }

                if (
                  cycleStations.length > 0 &&
                  cycleStations[cycleStations.length - 1]?.stopTime
                ) {
                  const stopTime = formatTime(
                    cycleStations[cycleStations.length - 1].stopTime
                  );
                  timeInfo += timeInfo
                    ? `  |  End: ${stopTime}`
                    : `End: ${stopTime}`;
                }

                if (timeInfo) {
                  doc.setFontSize(10);
                  doc.setFont("helvetica", "normal");
                  doc.setTextColor(...colors.mediumGray);
                  doc.text(timeInfo, leftMargin, yPosition);
                  yPosition += 12;
                }
              } catch (err) {
                console.warn("Error formatting time info:", err);
              }

              // Prepare table data
              const headers = ["Station", "Mode", "Time Range", "Cycle"];
              const tableData = [];

              // Get unique cycles
              let uniqueCycles = [];
              try {
                uniqueCycles = [
                  ...new Set(cycleStations.map((s) => s.cycle)),
                ].sort((a, b) => a - b);
              } catch (err) {
                uniqueCycles = [1];
                console.warn(
                  "Error getting unique cycles, using default:",
                  err
                );
              }

              // Add data for each cycle
              uniqueCycles.forEach((cycle) => {
                const cycleStationsFiltered = cycleStations.filter(
                  (station) => station.cycle === cycle
                );

                if (cycleStationsFiltered.length > 0) {
                  // Add cycle separator
                  tableData.push([`Cycle ${cycle}`, "", "", ""]);

                  cycleStationsFiltered.forEach((station) => {
                    try {
                      const stationName = safeText(
                        station.groupName || station.stationName
                      );
                      const stationMode = formatStationTime(station);
                      const startTime = formatOnlyTime(station.startTime) || "";
                      const stopTime = formatOnlyTime(station.stopTime) || "";
                      const timeRange = `${startTime} - ${stopTime}`;
                      const cycleNum = safeText(String(station.cycle));

                      tableData.push([
                        stationName,
                        stationMode,
                        timeRange,
                        cycleNum,
                      ]);
                    } catch (err) {
                      console.warn("Error processing station:", err);
                      tableData.push(["Error processing station", "", "", ""]);
                    }
                  });
                }
              });

              if (tableData.length > 0) {
                checkNewPage(60);
                drawTable(headers, tableData, yPosition);
              }
            } catch (err) {
              console.warn("Error processing cycle stations:", err);

              // Add error message
              checkNewPage(25);
              doc.setFillColor(252, 232, 230); // Light red
              doc.roundedRect(leftMargin, yPosition, pageWidth, 20, 3, 3, "F");

              doc.setFontSize(10);
              doc.setTextColor(...colors.accent);
              doc.text(
                `Error processing schedule group ${idx + 1}`,
                leftMargin + 8,
                yPosition + 12
              );
              yPosition += 30;
            }
          });
        } else {
          // No station data for this program
          checkNewPage(30);

          doc.setFillColor(254, 249, 195); // Light yellow
          doc.roundedRect(leftMargin, yPosition, pageWidth, 25, 3, 3, "F");

          doc.setFontSize(12);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...colors.secondary);
          doc.text(
            "⚠ No Station Data Available for this Program",
            leftMargin + 10,
            yPosition + 16
          );
          yPosition += 35;
        }

        // Add separator between programs (except for the last one)
        if (programIndex < mergedPrograms.value.length - 1) {
          checkNewPage(20);
          doc.setDrawColor(...colors.mediumGray);
          doc.setLineWidth(1);
          doc.line(
            leftMargin,
            yPosition,
            doc.internal.pageSize.width - rightMargin,
            yPosition
          );
          yPosition += 20;
        }
      } catch (error) {
        console.warn(`Error processing program ${program.programName}:`, error);

        // Add error message for this program
        checkNewPage(30);
        doc.setFillColor(252, 232, 230); // Light red
        doc.roundedRect(leftMargin, yPosition, pageWidth, 25, 3, 3, "F");

        doc.setFontSize(12);
        doc.setTextColor(...colors.accent);
        doc.text(
          `Error processing program: ${program.programName}`,
          leftMargin + 8,
          yPosition + 16
        );
        yPosition += 35;
      }
    }

    // Add professional footer
    const addFooter = () => {
      const footerY = doc.internal.pageSize.height - 15;

      // Footer line
      doc.setDrawColor(...colors.mediumGray);
      doc.setLineWidth(0.5);
      doc.line(
        leftMargin,
        footerY - 5,
        doc.internal.pageSize.width - rightMargin,
        footerY - 5
      );

      // Footer text
      const now = new Date();
      const timestamp = `Generated on ${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.mediumGray);
      doc.text(timestamp, leftMargin, footerY);

      // Page number on right
      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      const totalPages = doc.internal.pages.length - 1;
      doc.text(
        `${pageNum} / ${totalPages}`,
        doc.internal.pageSize.width - rightMargin - 15,
        footerY
      );
    };

    // Add footer to all pages
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      if (i > 1) addPageHeader();
      addFooter();
    }

    console.log("Saving PDF...");

    // Generate professional filename
    const titleName = safeText(props.title, "schedule").replace(
      /[^a-zA-Z0-9]/g,
      "_"
    );
    const dateStr = new Date().toISOString().split("T")[0];
    const fileName = `${titleName}_All_Programs_${dateStr}.pdf`;

    // Save the PDF
    doc.save(fileName);

    console.log("Professional PDF with all programs generated successfully!");

    // Success feedback
    setTimeout(() => {
      console.log("PDF download completed");
    }, 100);
  } catch (error) {
    console.error("PDF generation error:", error);
    console.error("Error details:", {
      message: error.message,
      stack: error.stack,
      props: props,
      mergedPrograms: mergedPrograms.value,
    });

    // User-friendly error notification
    const errorMsg = `Failed to generate PDF: ${error.message}\n\nPlease try again or contact support if the issue persists.`;
    alert(errorMsg);
  }
};
</script>

<style scoped>
.modal {
  @apply bg-black/20 backdrop-blur-sm w-full h-full fixed top-0 left-0 px-4 py-6 overflow-x-hidden overflow-y-auto z-[999999] justify-center items-start flex;
}

.modal-inner {
  background: white;
  @apply rounded-3xl border-2 border-black max-w-[900px] w-full h-fit;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-content {
  @apply flex flex-col;
  gap: 24px;
}

.header {
  @apply text-center border-2 border-black rounded-xl;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.week-title {
  @apply text-lg font-bold text-black;
  letter-spacing: 0.025em;
}

.tabs-section {
  @apply flex justify-center;
  margin: 8px 0;
}

.tabs-container {
  @apply flex gap-3 justify-center flex-wrap;
}

.tab-button {
  @apply px-5 py-3 border-2 border-black rounded-xl text-sm font-semibold bg-white hover:bg-gray-50 transition-all duration-200;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.tab-button.active {
  @apply bg-black text-white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.content-section {
  @apply space-y-7;
  gap: 28px;
}

.date-info-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.date-info-section:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.date-grid {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-10;
}

.date-column {
  @apply space-y-4;
  gap: 16px;
}

.date-header {
  @apply text-lg font-semibold text-gray-800;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 2px solid #cbd5e1;
  position: relative;
  letter-spacing: 0.025em;
}

.date-header::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 1px;
}

.date-list {
  @apply space-y-3;
  gap: 12px;
}

.date-item {
  @apply text-sm text-gray-700;
  padding: 8px 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(226, 232, 240, 0.5);
}

.date-item:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.date-bullet {
  color: #6366f1;
  font-size: 1.2em;
  margin-right: 8px;
  font-weight: bold;
}

.date-text {
  flex: 1;
  line-height: 1.4;
}

.day-name {
  margin-left: 8px;
  font-style: italic;
  color: #64748b;
  font-size: 0.9em;
}

.highlight-today {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid #f59e0b;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2);
}

.highlight-today .date-bullet {
  color: #f59e0b;
}

.highlight-today:hover {
  background: linear-gradient(135deg, #fde68a 0%, #fcd34d 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3);
}

.time-info-section {
  background: #f1f3f4;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 24px;
}

.time-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-8;
}

.time-column {
  gap: 12px;
  @apply space-y-3;
}

.time-header {
  @apply text-base font-bold text-black;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid #dee2e6;
}

.time-list {
  gap: 8px;
  @apply space-y-2;
}

.time-item {
  @apply text-sm text-gray-700;
  padding: 4px 0;
  font-weight: 500;
}

.schedule-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.schedule-title {
  @apply text-lg font-bold text-black;
  margin-bottom: 4px;
}

.schedule-subtitle {
  @apply text-sm text-gray-600;
  margin-bottom: 20px;
  font-weight: 500;
}

.station-table {
  @apply w-full border-2 border-black rounded-xl overflow-hidden text-center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 16px;
}

.station-header {
  padding: 16px 12px;
  @apply text-sm font-bold text-black text-center;
  background: linear-gradient(135deg, #f1f3f4 0%, #e9ecef 100%);
  border-bottom: 2px solid black;
}

.station-cell {
  padding: 14px 12px;
  @apply text-sm text-black text-center;
  border-bottom: 1px solid #e9ecef;
  font-weight: 500;
  vertical-align: middle;
}

.station-cell:last-child {
  border-bottom: none;
}

tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

tbody tr:hover {
  background-color: #e9ecef;
  transition: background-color 0.2s ease;
}

.navigation {
  @apply flex justify-between items-center;
  padding-top: 20px;
}

.nav-btn {
  @apply flex items-center text-sm text-black hover:text-gray-600 transition-colors cursor-pointer;
  gap: 8px;
  font-weight: 600;
}

.nav-btn.disabled {
  @apply text-gray-400 cursor-not-allowed hover:text-gray-400;
}

.nav-arrow {
  @apply text-lg font-bold;
}

.nav-text {
  @apply font-semibold;
}

.no-program-container,
.no-data {
  @apply text-center;
  padding: 48px 24px;
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 16px;
}

.no-program-message p,
.no-data-text {
  @apply text-gray-500 text-base;
  font-weight: 500;
}

.loading-container {
  padding: 24px 0;
}

.loading-skeleton {
  @apply animate-pulse bg-gray-200 rounded-xl;
  height: 160px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.drop-in-enter-active,
.drop-in-leave-active {
  transition: all 0.3s ease-out;
}

.drop-in-enter-from,
.drop-in-leave-to {
  opacity: 0;
  transform: translateY(-50px);
}

.cycle-navigation-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0 20px 0;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f1f3f4 0%, #e9ecef 100%);
  border-radius: 12px;
  border-left: 4px solid #007bff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.cycle-info .cycle-display {
  font-weight: 700;
  color: #495057;
  font-size: 15px;
}

.navigation-buttons {
  display: flex;
  gap: 12px;
}

.cycle-nav-btn {
  padding: 10px 16px;
  border: 2px solid #dee2e6;
  background-color: #ffffff;
  color: #495057;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 120px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.cycle-nav-btn:hover:not(.disabled) {
  background-color: #e9ecef;
  border-color: #adb5bd;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.cycle-nav-btn.disabled {
  background-color: #f8f9fa;
  color: #adb5bd;
  cursor: not-allowed;
  border-color: #e9ecef;
  box-shadow: none;
}

.prev-btn:not(.disabled) {
  border-color: #6c757d;
  color: #6c757d;
}

.prev-btn:hover:not(.disabled) {
  background-color: #6c757d;
  color: white;
}

.next-btn:not(.disabled) {
  border-color: #28a745;
  color: #28a745;
}

.next-btn:hover:not(.disabled) {
  background-color: #28a745;
  color: white;
}

.empty-cycle-state {
  text-align: center;
  padding: 32px 24px;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  margin-top: 16px;
}

.empty-cycle-message {
  color: #6c757d;
  font-style: italic;
  margin: 0;
  font-size: 15px;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-inner {
    padding: 20px;
    margin: 10px;
  }

  .date-grid,
  .time-grid {
    @apply grid-cols-1;
    gap: 20px;
  }

  .tabs-container {
    @apply flex-col;
    align-items: center;
  }

  .tab-button {
    min-width: 200px;
  }

  .cycle-navigation-controls {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .navigation-buttons {
    justify-content: center;
  }

  .station-table {
    font-size: 12px;
  }

  .station-header,
  .station-cell {
    padding: 10px 8px;
  }
}
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.pdf-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
}

.pdf-button:hover:not(:disabled) {
  background-color: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.pdf-button:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.pdf-button:active:not(:disabled) {
  transform: translateY(0);
}

.pdf-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .week-title {
    text-align: center;
    margin-bottom: 8px;
  }

  .pdf-button {
    justify-content: center;
    width: 100%;
  }
}
</style>
